import { Hsluv } from 'hsluv';

import { hexToHsl, type HSL } from './colorUtilities';

/**
 * Chroma-js based implementation for stable palette generation
 * Uses HSLuv for perceived mode and direct HSL manipulation for linear mode
 */
export default function createPalette(primaryColorHex: string, primaryColorHue = 600) {
  const h = 0;
  const s = 0;
  const lMin = 0;
  const lMax = 100;

  // All available stops (including 0 and 1000 for calculation)
  const allStops = [0, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950, 1000];

  // 1. Create hue scale
  const valueStopIndex = allStops.indexOf(primaryColorHue);
  if (valueStopIndex === -1) {
    throw new Error(`Invalid valueStop: ${primaryColorHue}`);
  }

  const hueScale = allStops.map((stop) => {
    const stopIndex = allStops.indexOf(stop);
    const diff = Math.abs(stopIndex - valueStopIndex);
    const tweakValue = h ? diff * h : 0;
    return { stop, tweak: tweakValue };
  });

  // 2. Create saturation scale
  const saturationScale = allStops.map((stop) => {
    const stopIndex = allStops.indexOf(stop);
    const diff = Math.abs(stopIndex - valueStopIndex);
    const tweakValue = s ? Math.round((diff + 1) * s * (1 + diff / 10)) : 0;
    return { stop, tweak: Math.min(tweakValue, 100) };
  });

  // 3. Create lightness distribution
  const hsluv = new Hsluv();
  hsluv.hex = primaryColorHex;
  hsluv.hexToHsluv();

  const lightnessValue = hsluv.hsluv_l;

  // Create the three anchor points
  const distributionAnchors = [
    { stop: 0, tweak: lMax },
    { stop: primaryColorHue, tweak: lightnessValue },
    { stop: 1000, tweak: lMin },
  ];

  // Interpolate for missing stops
  const distributionScale = allStops.map((stop) => {
    // If it's an anchor point, use the anchor value
    const anchor = distributionAnchors.find((a) => a.stop === stop);
    if (anchor) {
      return anchor;
    }

    // Otherwise interpolate between anchor points
    let leftAnchor, rightAnchor;

    if (stop < primaryColorHue) {
      leftAnchor = distributionAnchors[0]; // stop 0
      rightAnchor = distributionAnchors[1]; // primaryColorHue
    } else {
      leftAnchor = distributionAnchors[1]; // primaryColorHue
      rightAnchor = distributionAnchors[2]; // stop 1000
    }

    // Linear interpolation
    const range = rightAnchor.stop - leftAnchor.stop;
    const position = stop - leftAnchor.stop;
    const ratio = position / range;
    const tweak = leftAnchor.tweak + (rightAnchor.tweak - leftAnchor.tweak) * ratio;

    return { stop, tweak: Math.round(tweak) };
  });

  const swatches = allStops.reduce(
    (result, stop, stopIndex) => {
      if (stop === 0 || stop === 1000) return result;

      if (stop === primaryColorHue) {
        result[stop] = hexToHsl(primaryColorHex);
        return result;
      }

      // Get tweaks for this stop
      const hTweak = hueScale[stopIndex].tweak;
      const sTweak = saturationScale[stopIndex].tweak;
      const lTweak = distributionScale[stopIndex].tweak;

      // HSLuv for perceived mode
      const hsluv = new Hsluv();
      hsluv.hex = primaryColorHex;
      hsluv.hexToHsluv();

      // Handle grayscale colors in HSLuv (NaN hue)
      const normalizedHsluvH = isNaN(hsluv.hsluv_h) ? 0 : hsluv.hsluv_h;
      const newHsluvH = (normalizedHsluvH + hTweak) % 360;
      const newHsluvS = Math.max(0, Math.min(100, hsluv.hsluv_s + sTweak));
      const newHsluvL = Math.max(0, Math.min(100, lTweak));

      hsluv.hsluv_h = newHsluvH;
      hsluv.hsluv_s = newHsluvS;
      hsluv.hsluv_l = newHsluvL;
      hsluv.hsluvToHex();

      result[stop] = hexToHsl(hsluv.hex);
      return result;
    },
    {} as Record<number, HSL>,
  );

  return swatches;
}
